import { useCallback, useEffect, useState } from "react";
import {
  Avatar,
  Flex,
  Modal,
  Switch,
  Table,
  TablePaginationConfig,
} from "antd";
import styled from "styled-components";
import { editUser, getListUSers, TParamsUser } from "services/UserManagement";
import Search from "antd/es/input/Search";
import { User } from "services/auth";
import { SorterResult } from "antd/es/table/interface";
import AvatarDefault from "assets/default-avatar.jpg";
import { RoleType } from "services/login";
import { useSelector } from "react-redux";
import { useSelectAuthUser } from "features/authUserSlice";
import moment from "moment";

const initFilter = {
  page: 1,
  limit: 10,
  total: 0,
};

const UserManagement = () => {
  const { user } = useSelector(useSelectAuthUser);

  const [data, setData] = useState<User[]>([]);
  const [params, setParams] = useState<TParamsUser>(initFilter);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async (params?: TParamsUser) => {
    setIsLoading(true);
    try {
      const { data } = await getListUSers(params);
      setData(data.users.map((item, index) => ({ ...item, key: index })));
      setParams((pre) => ({
        ...pre,
        total: data.total,
        page: params?.page ?? pre.page,
      }));
    } catch (error: any) {
      Modal.error({
        content: error.response?.data.message || "Fetch data failed",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSearch = useCallback(
    (value: string) => {
      setParams({ ...params, search: value });
      fetchData({ ...params, search: value });
    },
    [fetchData, params]
  );

  const handleChangeTable = useCallback(
    (
      { current, pageSize }: TablePaginationConfig,
      _: any,
      { order, field }: SorterResult
    ) => {
      if (!current || !pageSize) return;
      fetchData({
        ...params,
        page: current!,
        limit: pageSize!,
        order: order ? (order === "ascend" ? "asc" : "desc") : "",
        sortBy: (field as string) || "",
      });
    },
    [fetchData, params]
  );

  const handleOkEdit = useCallback(
    async (data: User, fieldCheck: keyof User) => {
      try {
        await editUser(data.id, {
          ...data,
          [fieldCheck]: !data[fieldCheck],
        } as User);
        await fetchData(params);
        Modal.success({
          content: "Edit user success!",
        });
      } catch (error: any) {
        Modal.error({
          content: error.response?.data.message || "Edit failed",
        });
      }
    },
    [fetchData, params]
  );

  const handleConfirmCheckBox = useCallback(
    (data: User, field: "isActive" | "isVerifiedPhone" | "isVerifiedEmail") => {
      Modal.confirm({
        title: "Are you sure edit this user?",
        onOk: () => handleOkEdit(data, field),
      });
    },
    [handleOkEdit]
  );

  const columns = [
    {
      title: "Avatar",
      dataIndex: "avatar",
      key: "avatar",
      render: (text: string) => (
        <Avatar src={text || AvatarDefault} alt="avatar user" />
      ),
    },
    {
      title: "Display Name",
      dataIndex: "firstName",
      key: "firstName",
      sorter: true,
      render: (_: string, { firstName, lastName }: User) => (
        <p>{`${firstName || ""} ${lastName || ""}`}</p>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: true,
      render: (text: string) => <p>{text}</p>,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (text: string) => <p>{text}</p>,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (text: string) => <p>{text}</p>,
    },
    {
      title: "Date of birth",
      dataIndex: "dateOfBirth",
      key: "dateOfBirth",
      render: (text: string) => (
        <p>{text ? moment(text).format("MM/DD/YYYY") : ""}</p>
      ),
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      render: (text: string) => <p>{text}</p>,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (text: string) => <p>{text}</p>,
    },
    {
      title: "Created at",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      render: (text: string) => <p>{moment(text).format("MM/DD/YYYY")}</p>,
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      render: (v: boolean, record: User) => (
        <Switch
          checked={v}
          disabled={RoleType.ADMIN === record.role || user?.id === record.id}
          onClick={() => handleConfirmCheckBox(record, "isActive")}
        />
      ),
    },
    {
      title: "Verified Email",
      dataIndex: "isVerifiedEmail",
      key: "isVerifiedEmail",
      render: (v: boolean, record: User) => (
        <Switch
          checked={v}
          disabled={RoleType.ADMIN === record.role || user?.id === record.id}
          onClick={() => handleConfirmCheckBox(record, "isVerifiedEmail")}
        />
      ),
    },
    {
      title: "Verified Phone",
      dataIndex: "isVerifiedPhone",
      key: "isVerifiedPhone",
      render: (v: boolean, record: User) => (
        <Switch
          checked={v}
          disabled={RoleType.ADMIN === record.role || user?.id === record.id}
          onClick={() => handleConfirmCheckBox(record, "isVerifiedPhone")}
        />
      ),
    },
  ];

  return (
    <WrapperUserManagement>
      <h1>User Management</h1>
      <Flex style={{ width: "40%", maxWidth: 350, minWidth: 200 }}>
        <Search placeholder="Search by email or name" onSearch={onSearch} />
      </Flex>
      <Table<User>
        loading={isLoading}
        dataSource={data}
        scroll={{ x: "max-content" }}
        onChange={handleChangeTable as any}
        pagination={{
          current: params.page,
          pageSize: params.limit,
          total: params.total,
          showSizeChanger: false,
        }}
        columns={columns}
      />
    </WrapperUserManagement>
  );
};

export default UserManagement;

const WrapperUserManagement = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: 20px;
  &.ant-pagination {
    margin: 8px 0px !important;
  }
  > h1 {
    margin: 0;
  }
`;
